/**
 * `auto_number` — **no value type at all** (`specs/M2-tier2-column-types.md` §5.14,
 * `[09§A.7 auto_number]`, `[cvs§2 auto_number]`).
 *
 * Verbatim: *"Values cannot be read, filtered, updated, or cleared because they are computed
 * client-side."* There is no `AutoNumberValue` in the 2026-10 schema, and **no cell of this
 * type was ever returned** by the API even though five active boards declare one — but the
 * activity log does carry `column_type: "autonumber"` rows, so the column is real and its
 * value changes are logged.
 *
 * We therefore compute it the only way that is defensible: the item's **position in the board**
 * (1-based), with `settings.prefix` and the sequence direction applied for display. Since
 * monday never exposed a value, there is nothing to be byte-compatible with — the importer
 * renumbers on import (`specs/M12-X2-importer.md`), which is what `materialise: 'never'` plus
 * `api: { value: 'null', text: 'null' }` reproduces: our `/v2` stays as silent as monday's.
 */
import { z } from 'zod';
import { ColumnValueError, defineColumnType } from '../types.js';
import { autoNumberFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const autoNumberValueSchema = z.looseObject({ number: z.number().int() });
export const autoNumberSettingsSchema = z.looseObject({
    /** `"board"` in the only observed settings blob `[09§A.7 auto_number]`. */
    scope: z.string().optional(),
    prefix: z.string().optional(),
    /** `"ascending"` | `"descending"`. */
    order: z.string().optional(),
    font: z.string().optional(),
    color: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
export const autoNumberColumn = defineColumnType({
    type: 'auto_number',
    title: 'Auto Number',
    description: 'A sequence number assigned by the board. Read-only.',
    /** No `AutoNumberValue` type exists in the schema `[cvs§2 auto_number]`. */
    apiFragmentName: null,
    legacyTypeName: 'autonumber',
    idPrefix: 'auto_number',
    valueSchema: autoNumberValueSchema,
    settingsSchema: autoNumberSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: autoNumberSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    /** *"cannot be … filtered"* `[09§A.7]`. */
    filterConditions: autoNumberFilterConditions,
    filterOperators: operatorsOf(autoNumberFilterConditions),
    /** Nothing is stored, so nothing can be aggregated in SQL (spec §5.14). */
    aggregations: ['none'],
    /** monday returns no cell at all; an absent cell reads as `text: null`. */
    emptyText: null,
    computed: {
        dependsOn() {
            return [{ kind: 'item_meta', field: 'position' }];
        },
        compute(ctx) {
            /*
             * The read path supplies the 1-based **board rank** in `item.position` (T-9p). Before
             * that it was read off `item.id`, which is a monday bigint, so an auto-number column on
             * a real board rendered `1165541063` instead of `1`. With no rank we have nothing to
             * show — never a fallback to the id.
             */
            const position = ctx.item?.position;
            if (position === undefined || position === null)
                return null;
            const number = Number(position);
            return Number.isSafeInteger(number) && number > 0 ? { number } : null;
        },
        materialise: 'never',
        /** monday exposes nothing: `value` and `text` stay null on `/v2` (M2 tier-2 §7). */
        api: { value: 'null', text: 'null' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('auto_number', 'the auto number column is assigned by the board');
    },
    toText(value, settings) {
        if (value === null)
            return null;
        return `${settings.prefix ?? ''}${value.number}`;
    },
    toSortKeys(value) {
        return { text: null, number: value?.number ?? null, date: null };
    },
    isEqual(a, b) {
        return (a?.number ?? null) === (b?.number ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { number: value.number };
    },
    fromMondayValue() {
        // Nothing to import: monday never returns a cell (`[cvs§2 auto_number]`).
        return null;
    },
    toMondayValue() {
        return null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return autoNumberSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment() {
        return {};
    },
    toWebhookValue() {
        return null;
    },
    filterSql() {
        return null;
    },
    /**
     * **The sequence, not a shadow (P-051 audit).** `materialise: 'never'` means no `cell_values`
     * row exists for this column on any board, so ordering on the three shadows compared NULLs and
     * did nothing. The value *is* the item's 1-based rank in board reading order — group position,
     * then `items.position`, then `items.id` (`boardRanks` in `services/board-read.ts`) — and sorts
     * run **inside a group** (`specs/M1-B6-filters-sort.md` §4.5), where the group term is constant.
     * Ordering on `(position, id)` is therefore exactly ordering on the number the cell renders.
     *
     * monday's own sort picker does not offer `Auto Number` at all (§4.5: *"`Auto Number` is
     * neither"*). This is not a behaviour we invent for that picker — it is what our `/v2` and any
     * saved sort must do if they name the column, instead of silently returning the board order.
     */
    sortSql(refs) {
        return [`${refs.item}.position`, `${refs.item}.id`];
    },
    summarySql() {
        return null;
    },
});
//# sourceMappingURL=index.js.map